import React from "react";
import { useTranslation } from "react-i18next";
import { UiVersion } from "../../uiVersion";
import { useVersionSwitch } from "./useVersionSwitch";

// GUNDI-5639 / GUNDI-5642: the way OUT of the new portal and back to the frozen
// legacy one. New code (not a frozen import), mounted by the new tree's Nav.
//
// Not dismissible (product call): the switch is the whole point of the trial,
// so it stays on screen wherever it is shown.
type VersionBannerProps = { variant: UiVersion };

// GUNDI-5642: the new tree's banner shows on EVERY page — since legacy is the
// default, the way back must never be more than one click away — so there is
// nothing left to match on. The `legacy` variant is commented out below rather
// than deleted: it comes back in a later release, when the new version is
// advertised in legacy again.
//
// const DETAIL_PATHS: Record<UiVersion, RegExp[]> = {
//     new: [/^\/routes\/(?!new(\/|$))[^/]+/, /^\/connections\/(?!new(\/|$))[^/]+/],
//     legacy: [/^\/connections\/(?!new(\/|$))[^/]+/, /^\/destinations?\/(?!new(\/|$))[^/]+/],
// };
// Kept as the single source of truth shared with the padding wrappers, so the
// strip and the 28px compensation can never disagree.
export function useVersionBannerVisible(variant: UiVersion): boolean {
    return variant === "new";
}

export default function VersionBanner({ variant }: VersionBannerProps) {
    const { t } = useTranslation();
    const { switchTo, switching } = useVersionSwitch();

    // Only the new tree advertises a switch today (GUNDI-5642); in legacy the
    // way over is the hidden unlock in the nav, not a banner.
    if (variant !== "new") return null;

    // Slim full-width strip mounted inside the fixed nav wrapper, directly
    // under the environment stripe. Kept ≤28px tall: the app shell's
    // pt-[143px] leaves room for the banner below the 86px nav without
    // covering page content.
    return (
        <div
            data-testid="VersionBanner-div"
            className="flex flex-row items-center justify-center gap-2 px-4 h-7 bg-white border-b border-gray-lines"
        >
            <span className="text-xs font-normal text-off-black">
                {t("You're viewing the new version of Gundi.")}
            </span>
            <button
                type="button"
                data-testid="VersionBanner-button"
                disabled={switching}
                onClick={() => switchTo("legacy")}
                className="text-xs font-semibold underline underline-offset-2 disabled:opacity-50 text-secondary-text hover:text-off-black"
            >
                {t("Switch back to the previous version")}
            </button>
        </div>
    );
}
